import * as React from "react";
import {
  Tabs,
  Tab,
  Container,
  Grid,
  makeStyles,
  createStyles,
  Theme,
  Hidden,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import InstallationFilter from "../InstallationFilter";
import InstallationInformation from "../../../components/InstallationInformation";
import InstallationModel from "../../../core/models/Installation";
import { useHubx, useHubXSubscribe } from "../../../HubX";
import { useParams, useHistory } from "react-router-dom";
import {
  FETCH_CACHEABLE_CUSTOMERS,
  FETCH_CUSTOMERS,
  FETCH_INSTALLATION,
  FILTER_ALL_INSTALLATIONS_NEW,
} from "../../../Fedx";
import useConstructor from "../../../core/hooks/useConstructor";
import get from "lodash/get";
import BaseFilter from "../../../core/models/BaseFilter";
import CustomerModel from "../../../core/models/Customer";
import InstallationLevelTab from "../InstallationLevelTab";
import clsx from "clsx";
import HomeButton from "../../../components/HomeButton";
import InstallationLevelReportTab from "../InstallationLevelReportTab";
import InstallationLevelHistory from "../InstallationLevelHistory";
import InstallationConsumptionHistory from "../InstallationConsumptionHistory";
import InstallationConsumption from "../InstallationConsumption";
import {
  PermissionsType,
  PERMISSION_ACCESS_ALL_INSTALLATIONS,
  PERMISSION_ACCESS_BASE,
  PERMISSION_ACCESS_CONSUMPTION_CHART,
  PERMISSION_ACCESS_CONSUMPTION_HISTORY,
  PERMISSION_ACCESS_INFORM_LEVEL,
  PERMISSION_ACCESS_LEVEL_CHART,
  PERMISSION_ACCESS_LEVEL_HISTORY,
  PERMISSION_ACCESS_TRIP_HISTORY,
} from "../../../core/models/Permission";
import ProtectedRender from "../../ProtectedRender";
import useAuth from "../../../core/hooks/useAuth";
import { isArrayValid } from "../../../helpers/utility";
import { EVENT_REFRESH_INSTALLATION_DETAIL_PAGE } from "../../../Events";
import { useSnackbar } from "notistack";
import { IInstallation } from "../../../core/contracts/IInstallation";
import { indexOf, isNull } from "lodash";
import User from "../../../core/models/User";
import { SessionCache } from "../../../helpers/cache";
import AllInstallationsFilter from "../../../core/models/AllInstallationsFilter";
import InstallationGroup from "../../../core/models/InstallationGroup";
import InstallationTripsHistory from "../InstallationTripsHistory"
import { TITLE_PAGE } from "../../../core/constants/appGlobals";
import GoogleAnalyticsService from "../../../services/GoogleAnalyticsService";


const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    tabsWrapper: {
      "paddingTop": theme.spacing(2),
      "display": "flex",
      "alignItems": "flex-start",
      "columnGap": theme.spacing(1.5),

      // home icon
      "& > *:first-child": {
        paddingTop: 4,
      },
    },

    tabsRoot: {
      "borderBottom": `1px solid ${theme.customPalette.border.gray}`,
      "marginBottom": theme.spacing(3),
      "flex": 1,

      "minHeight": theme.spacing(4),

      "& .MuiTab-root": {
        minHeight: theme.spacing(4),
      },
    },

    main: {
      "marginTop": theme.spacing(3),
      "justifyContent": "space-between",
      "columnGap": theme.spacing(2),

      "& .hidden": {
        display: "none",
      },

      "& .main-component": {
        flex: 1,
        maxWidth: "76%",

        [theme.breakpoints.down("xs")]: {
          maxWidth: "95%",
        },
      },

      "& .information": {
        flexShrink: 1,
      },

      "& .tab-content": {
        "height": "100%",

        "& > *": {
          height: "100%",
        },
      },
    },
  })
);

export const tabLevelChartLabel = "Níveis";
export const tabConsumptionChartLabel = "Consumo";
export const tabLevelHistoryLabel = "Histórico Nível";
export const tabConsumptionHistoryLabel = "Histórico Consumo";
export const tabTripsHistoryLabel = "Histórico de Abastecimentos";
export const tabSendLevelLabel = "Informar Nível";

const tabs = [
  tabLevelChartLabel,
  tabConsumptionChartLabel,
  tabLevelHistoryLabel,
  tabConsumptionHistoryLabel,
  tabTripsHistoryLabel,
  tabSendLevelLabel,
] as const;

export const tabMobileInformation = "Informações";

const getTabPermissionFromLabel = (label: typeof tabs[number]) => {
  switch (label) {
    case tabLevelChartLabel:
      return PERMISSION_ACCESS_LEVEL_CHART;
    case tabConsumptionChartLabel:
      return PERMISSION_ACCESS_CONSUMPTION_CHART;
    case tabLevelHistoryLabel:
      return PERMISSION_ACCESS_LEVEL_HISTORY;
    case tabConsumptionHistoryLabel:
      return PERMISSION_ACCESS_CONSUMPTION_HISTORY;
    case tabSendLevelLabel:
      return PERMISSION_ACCESS_INFORM_LEVEL;
    case tabTripsHistoryLabel:
      return PERMISSION_ACCESS_TRIP_HISTORY;
    default:
      return PERMISSION_ACCESS_BASE;
  }
};

const getTabPermissionFromEnum = (label: PermissionsType) => {
  switch (label) {
    case PERMISSION_ACCESS_LEVEL_CHART:
      return tabLevelChartLabel;
    case PERMISSION_ACCESS_CONSUMPTION_CHART:
      return tabConsumptionChartLabel;
    case PERMISSION_ACCESS_LEVEL_HISTORY:
      return tabLevelHistoryLabel;
    case PERMISSION_ACCESS_CONSUMPTION_HISTORY:
      return tabConsumptionHistoryLabel;
    case PERMISSION_ACCESS_INFORM_LEVEL:
      return tabSendLevelLabel;
    case PERMISSION_ACCESS_TRIP_HISTORY:
      return tabTripsHistoryLabel;
    default:
      return PERMISSION_ACCESS_LEVEL_CHART;
  }
};

const getTabValueFromKey = (key: "level" | "consumption" | "send_level") => {
  const dic = {
    level: tabLevelChartLabel,
    consumption: tabConsumptionChartLabel,
    send_level: tabSendLevelLabel,
  };

  return dic[key];
};

function InstallationMainCard() {
  const classes = useStyles();
  const params = useParams();
  const history = useHistory();
  const authProvider = useAuth();
  const theme = useTheme();
  const isMobileScreenSize = useMediaQuery(theme.breakpoints.down("xs"));
  const toastr = useSnackbar();

  const currentUser: User | undefined = React.useMemo(() => {
    const cache = new SessionCache();
    const userInfo: User | null = cache.get("user");

    if (userInfo) {
      return userInfo;
    }
  }, []);

  const hub = useHubx();

  const selectTabFromRoute = React.useCallback(
    (current?: string): string => {
      const initialTabFromRoute = get(params, "initial_tab");

      if (!initialTabFromRoute && current) {
        return current;
      }

      const index = getTabValueFromKey(initialTabFromRoute);

      if (index) {
        return index;
      }

      const newCustomerId = get(params, "customer_id");

      const group = currentUser?.installationGroup?.find((x) => x.id == newCustomerId);

      const first = get(group, "permissionList[0]")?.type || isNull;

      const firstName = getTabPermissionFromEnum(first);

      const tabWithPermission = tabs.find((e) => e == firstName);

      if (tabWithPermission) {
        return tabWithPermission;
      }

      return tabLevelChartLabel;
    },
    [params]
  );

  const [currentTab, setCurrentTab] = React.useState<string>(() => {
    const initialTab = selectTabFromRoute();
    return initialTab;
  });

  const [customers, setCustomers] = React.useState<CustomerModel[] | undefined>(undefined);
  const [customerInstallations, setCustomerInstallations] = React.useState<
    CustomerModel | undefined
  >(undefined);
  const [installation, setInstallation] = React.useState<InstallationModel | undefined>(undefined);
  const [loading, setLoading] = React.useState(false);

  React.useMemo(() => {
    const fullTitle = `${TITLE_PAGE}${ " - " + currentTab}`;

    document.title = fullTitle;
  }, [currentTab])

  React.useLayoutEffect(() => {
    const newTabIndex = selectTabFromRoute(currentTab);
    setCurrentTab(newTabIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectTabFromRoute]);

  // Effect for change in route affect component
  React.useLayoutEffect(() => {
    const customerId = get(params, "customer_id");

    if (customerId && isArrayValid(customers)) {
      const currentCustomerInstallations = customers?.find((x) => x.customer_id === customerId);
      setCustomerInstallations(currentCustomerInstallations);

      const installationId = get(params, "installation_id");

      if (installationId) {
        const currentInstallation = currentCustomerInstallations?.installations?.find(
          (x) => x.installation_id === installationId
        );

        if (currentInstallation) {
          if (!currentInstallation.has_rtu) {
            setCurrentTab(tabSendLevelLabel);
          }
          return setInstallation(currentInstallation);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const fetchInstallationData = async (installationId: string) => {
    setLoading(true);
    try {
      const params = {
        installation_name_id: installationId,
        installation_has_rtu: 1,
        return_ids: false,
      } as AllInstallationsFilter;
      const installationData: IInstallation[] = await hub.request(
        FILTER_ALL_INSTALLATIONS_NEW,
        params
      );
      setInstallation(installationData ? installationData[0] : undefined);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log("🚀 ~ file: index.tsx ~ line 249 ~ fetchInstallationData ~ error", error);
      toastr.enqueueSnackbar("Ops! Ocorreu um erro ao obter os dados desta instalação!", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const customerId = React.useMemo(() => get(params, "customer_id"), [params]);

  const refreshCustomerInstallations = React.useCallback(
    (customers: CustomerModel[]) => {
      const installationId = get(params, "installation_id");
      setCustomers(customers);
      const currentCustomerInstallations = customers.find((x) => x.customer_id == customerId);
      setCustomerInstallations(currentCustomerInstallations);

      if (installationId) {
        const currentInstallation = currentCustomerInstallations?.installations?.find(
          (x) => x.installation_id === installationId
        );

        if (currentInstallation) {
          if (!currentInstallation.has_rtu) {
            setCurrentTab(tabSendLevelLabel);
          }

          return setInstallation(currentInstallation);
        } else {
          if (
            (customerId === 0 || customerId === "0") &&
            authProvider.hasHighLevel() &&
            authProvider.hasPermission(PERMISSION_ACCESS_ALL_INSTALLATIONS)
          ) {
            return fetchInstallationData(installationId);
          }
        }
      }

      setInstallation(get(customers, "installations[0]"));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useConstructor(async () => {
    setLoading(true);
    try {
      const APIResponse: CustomerModel[] = await hub.request(FETCH_CACHEABLE_CUSTOMERS);
      refreshCustomerInstallations(APIResponse);
    } catch (Error) {
      // eslint-disable-next-line no-console
      console.error("InstallationMainCard -> Error", Error);
    } finally {
      setLoading(false);
    }
  });

  const refreshCustomersData = React.useCallback(async () => {
    setLoading(true);
    try {
      const APIResponse = await hub.request(FETCH_CUSTOMERS);
      refreshCustomerInstallations(APIResponse);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("InstallationMainCard -> error", error);
      toastr.enqueueSnackbar(
        "Ops! Ocorreu um erro ao tentar atualizar os dados desta instalação! Nenhuma modificação foi feita!",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useHubXSubscribe(EVENT_REFRESH_INSTALLATION_DETAIL_PAGE, refreshCustomersData);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleChange = (_: any, newValue: string) => {
    setCurrentTab(newValue);
    new GoogleAnalyticsService().sendPageViewEvent();
  };

  function handleChangeInstallation(event: React.ChangeEvent<{ value: string }>) {
    const selectedInstallationId = event.target.value;

    history.replace(`/installation/${get(params, "customer_id")}/${selectedInstallationId}`);
    const installation = customerInstallations?.installations?.find(
      (x) => x.installation_id === selectedInstallationId
    );

    if (installation) {
      if (!installation.has_rtu) {
        setCurrentTab(tabSendLevelLabel);
      }
      setInstallation(installation);
    }
  }

  function handleChangeCustomer(event: React.ChangeEvent<{ value: string }>) {
    const selectedCustomerId = event.target.value;
    const selectedCustomer = customers?.find((x) => x.customer_id === selectedCustomerId);
    const firstInstallation: InstallationModel = get(selectedCustomer, "installations[0]");
    history.replace(`/installation/${selectedCustomerId}/${firstInstallation.installation_id}`);

    setCustomerInstallations(selectedCustomer);
    setInstallation(firstInstallation);
  }

  const buildFilterFromInstallation = (): BaseFilter | undefined => {
    if (customerInstallations?.installations) {
      return {
        value: installation?.installation_id || "",
        id: "installationFilter",
        label: "",
        items:
          customerInstallations?.installations?.map((installation) => {
            return {
              key: installation.installation_id,
              value:
                `${installation.product_code} #${installation.installation_id}${
                  !installation.has_rtu ? " - sem RTU" : ""
                }` || "",
            };
          }) || [],
        handleChange: (e) => handleChangeInstallation(e),
      };
    }
  };

  const permissionToAccess = React.useMemo(() => {
    const indexTab = indexOf(tabs, currentTab);
    return getTabPermissionFromLabel(tabs[indexTab]);
  }, [currentTab]);

  const permitedGroupIds = React.useMemo(() => {
    if (customers && permissionToAccess) {
      return customers.map((x) => {
        const hasPermission = authProvider.hasPermission(permissionToAccess, x);
        if (hasPermission) {
          return x.customer_id;
        }
      });
    }
  }, [customers, permissionToAccess]);

  const buildFilterFromCustomers = (): BaseFilter | undefined => {
    if (customers) {
      return {
        value: customerInstallations?.customer_id || "",
        id: "customerFilter",
        label: "",
        items:
          customers
            .filter((e) => permitedGroupIds?.includes(e.customer_id))
            .map((customer) => {
              return { key: customer.customer_id, value: customer.customer_name };
            }) || [],
        handleChange: (e) => handleChangeCustomer(e),
      };
    }
  };

  const hasMoreThanOneTank = React.useMemo(() => get(installation, "number_of_tanks", 0) > 1, [
    installation,
  ]);

  const installationInSomeGroup = React.useMemo(() => {
    const installationId = get(params, "installation_id");
    return authProvider.hasInstallationInSomeGroup(installationId);
  }, [authProvider, params]);

  const useTabs = isMobileScreenSize
    ? [
        ...tabs
          .filter(
            (tab) => installation?.has_rtu || (!installation?.has_rtu && tab === tabSendLevelLabel)
          )
          .filter((tab) => {
            const permission = getTabPermissionFromLabel(tab);

            if (permission === PERMISSION_ACCESS_INFORM_LEVEL && !installationInSomeGroup)
              return false;

            return (
              authProvider.hasPermission(permission, customerInstallations) &&
              !authProvider.mobileRestriction([permission])
            );
          }),
        tabMobileInformation,
      ]
    : tabs
        .filter(
          (tab) => installation?.has_rtu || (!installation?.has_rtu && tab === tabSendLevelLabel)
        )
        .filter((tab) => {
          const permission = getTabPermissionFromLabel(tab);

          if (permission === PERMISSION_ACCESS_INFORM_LEVEL && !installationInSomeGroup)
            return false;

          return authProvider.hasPermission(permission, customerInstallations);
        });

  return (
    <Container component="main" maxWidth="xl">
      <div className={classes.tabsWrapper}>
        <HomeButton />
        <Tabs
          className={classes.tabsRoot}
          value={currentTab}
          onChange={handleChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          aria-label="installation-tabs-navigation-scrollable"
        >
          {[...useTabs].map((tab, idx) => (
            <Tab
              label={tab}
              onClick={() => handleChange(undefined, tab)}
              key={`installation-tab-${tab}-${idx}`}
              value={tab}
            />
          ))}
        </Tabs>
      </div>

      <InstallationFilter
        tanks={installation?.tanks}
        installationFilter={buildFilterFromInstallation()}
        customerFilter={buildFilterFromCustomers()}
        isLoading={loading}
        hasMoreThanOneTank={hasMoreThanOneTank}
        selectedTab={currentTab}
      />

      <Grid className={classes.main} container>
        <Grid item xs="auto" className={"main-component"}>
          {/* Change content based on tab value */}
          {/* Use protectedRender in order to optimize when user don't need to see content */}
          <ProtectedRender permission={getTabPermissionFromLabel(tabs[0])}>
            <div className={clsx(currentTab !== tabLevelChartLabel && "hidden", "tab-content")}>
              <InstallationLevelTab
                hasMoreThanOneTank={hasMoreThanOneTank}
                installationId={get(params, "installation_id")}
                tanks={installation?.tanks}
              />
            </div>
          </ProtectedRender>

          <ProtectedRender permission={getTabPermissionFromLabel(tabConsumptionChartLabel)}>
            <div
              className={clsx(currentTab !== tabConsumptionChartLabel && "hidden", "tab-content")}
            >
              <InstallationConsumption installation={installation} />
            </div>
          </ProtectedRender>

          <ProtectedRender permission={getTabPermissionFromLabel(tabLevelHistoryLabel)}>
            <div className={clsx(currentTab !== tabLevelHistoryLabel && "hidden", "tab-content")}>
              <InstallationLevelHistory installation={installation} />
            </div>
          </ProtectedRender>

          <ProtectedRender permission={getTabPermissionFromLabel(tabConsumptionHistoryLabel)}>
            <div
              className={clsx(currentTab !== tabConsumptionHistoryLabel && "hidden", "tab-content")}
            >
              <InstallationConsumptionHistory installation={installation} />
            </div>
          </ProtectedRender>

          <ProtectedRender permission={getTabPermissionFromLabel(tabTripsHistoryLabel)}>
            <div
              className={clsx(currentTab !== tabTripsHistoryLabel && "hidden", "tab-content")}
            >
              <InstallationTripsHistory installation={installation} />
            </div>
          </ProtectedRender>

          {installationInSomeGroup && (
            <ProtectedRender permission={getTabPermissionFromLabel(tabSendLevelLabel)}>
              <div className={clsx(currentTab !== tabSendLevelLabel && "hidden", "tab-content")}>
                <InstallationLevelReportTab
                  installationLastReading={installation?.installation_level}
                  hasRTU={Boolean(installation?.has_rtu)}
                  installationId={get(params, "installation_id")}
                  tanks={installation?.tanks}
                />
              </div>
            </ProtectedRender>
          )}

          <Hidden smUp>
            <div className={clsx(currentTab !== tabMobileInformation && "hidden", "tab-content")}>
              <InstallationInformation
                customer={customerInstallations}
                isLoading={loading}
                installation={installation}
              />
            </div>
          </Hidden>
        </Grid>

        {/* Fixed content*/}
        <Hidden xsDown>
          <Grid item xs={"auto"} className={"information"}>
            <InstallationInformation
              customer={customerInstallations}
              isLoading={loading}
              installation={installation}
            />
          </Grid>
        </Hidden>
      </Grid>
    </Container>
  );
}

export default InstallationMainCard;
